/* eslint eqeqeq: [2, "smart"] */

// Utils
import { getDeviceFingerprint } from '@shopee-user/device-fingerprint';

// Enums
import { Env, Locale } from '@shopee-user/global';

// Types
import {
  Config,
  EnvToLocaleToConfig,
  FeatureToggleConfig,
  LocaleToConfig,
} from './types';
import { NonLiveStaticToggleOverrideFeatureToggleConfig } from './nonLiveStaticToggleOverrideConfig';

// calculate once for consistency
const currentTimestamp = Math.floor(new Date().getTime() / 1000);

export function isFeatureEnabled<T extends string>(
  feature: T,
  featureToggleConfig: FeatureToggleConfig<T>,
  {
    defaultValue,
    env,
    locale,
    deviceFingerprint,
  }: {
    defaultValue: boolean;
    env: Env;
    locale: Locale;
    deviceFingerprint?: number;
  }
): boolean {
  const envToLocaleToConfig: EnvToLocaleToConfig | undefined | boolean =
    featureToggleConfig[feature];

  // 1. if envToLocaleToConfig is undefined or boolean, returns immediately
  if (envToLocaleToConfig === undefined) {
    return defaultValue;
  }

  if (typeof envToLocaleToConfig === 'boolean') {
    return envToLocaleToConfig;
  }

  const localeToConfig: LocaleToConfig | boolean | undefined =
    envToLocaleToConfig[env] ??
    envToLocaleToConfig['default_env'] ??
    envToLocaleToConfig['default'] ??
    undefined;

  // 2. else, if localeToConfig is undefined or boolean, returns immediately
  if (localeToConfig === undefined) {
    console.error(
      'User Static Feature Toggle:',
      `Invalid ${feature} config found for "${env}" Env:`,
      JSON.stringify(envToLocaleToConfig, null, 2)
    );
    return defaultValue;
  }

  if (typeof localeToConfig === 'boolean') {
    return localeToConfig;
  }

  const config: Config | boolean | undefined =
    localeToConfig[locale] ??
    localeToConfig['default_locale'] ??
    localeToConfig['default'] ??
    undefined;

  // 3. else, if config is undefined or boolean, returns immediately
  if (config === undefined) {
    console.error(
      'User Static Feature Toggle:',
      `Invalid ${feature} config found for "${env}" Env and "${locale}" Locale:`,
      JSON.stringify(localeToConfig, null, 2)
    );
    return defaultValue;
  }

  if (typeof config === 'boolean') {
    return config;
  }

  // 4. else, if currentTimestamp is not in [start_time, end_time), returns defaultValue
  if (config.start_time != null && currentTimestamp < config.start_time) {
    return defaultValue;
  }

  if (config.end_time != null && currentTimestamp >= config.end_time) {
    return defaultValue;
  }

  // 5. else, for each rule, if matches, then returns
  if (Array.isArray(config.rules)) {
    for (const rule of config.rules) {
      if (rule.disabled === true) {
        continue;
      }
      if (rule.start_time != null && currentTimestamp < rule.start_time) {
        continue;
      }
      if (rule.end_time != null && currentTimestamp >= rule.end_time) {
        continue;
      }
      const df = deviceFingerprint ?? getDeviceFingerprint();
      if (df % 100 < rule.percentage) {
        return rule.value;
      }
    }
  }

  // 6. else, return config.default
  return config.default;
}

/**
 * @returns boolean | undefined. If undefined is returned, use base feature toggle web's evaluated nonLiveValue
 */
export function getNonLiveStaticToggleOverrideValue(
  featureName: string,
  nonLiveStaticToggleOverrideConfig: NonLiveStaticToggleOverrideFeatureToggleConfig,
  locale: Locale
) {
  const nonLiveStaticToggleOverrideConfigInfo =
    nonLiveStaticToggleOverrideConfig[featureName];
  if (typeof nonLiveStaticToggleOverrideConfigInfo === 'undefined') {
    return undefined;
  }

  if (typeof nonLiveStaticToggleOverrideConfigInfo === 'boolean') {
    return nonLiveStaticToggleOverrideConfigInfo;
  }

  if (
    typeof nonLiveStaticToggleOverrideConfigInfo.nonLiveValue !== 'undefined'
  ) {
    const localeStaticToggleOverrideConfigInfo =
      nonLiveStaticToggleOverrideConfigInfo.nonLiveValue[locale];
    if (typeof localeStaticToggleOverrideConfigInfo !== 'undefined') {
      return localeStaticToggleOverrideConfigInfo;
    }
    return undefined;
  }

  return undefined;
}
